//@ts-check
import { useState, useRef, useEffect } from "react"
import { makeStyles, withStyles } from "@material-ui/core/styles"
import FolderIcon from "@material-ui/icons/Folder"
import FolderOpenIcon from "@material-ui/icons/FolderOpen"
import DescriptionIcon from "@material-ui/icons/Description"
import DownloadIcon from "@material-ui/icons/CloudDownload"
import IconButton from "@material-ui/core/IconButton"
import PublishIcon from "@material-ui/icons/Publish"
import NoteAddIcon from "@material-ui/icons/NoteAdd"
import CreateNewFolderIcon from "@material-ui/icons/CreateNewFolder"
import DeleteIcon from "@material-ui/icons/Delete"
import EditIcon from "@material-ui/icons/Edit"
import MoreVertIcon from "@material-ui/icons/MoreVert"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import DoneIcon from "@material-ui/icons/Done"
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep"
import Popover from "@material-ui/core/Popover"
//@ts-ignore
import _ from "lodash"
import clsx from "clsx"
import eyuiTheme from "../theme"
import { useTranslation } from "react-i18next"

import getFileContract from "../utils/get-file-contract"
import Button from "./button"
import Tooltip from "./tooltip"
import DropdownMenu from "./dropdown-menu"
import ClearAllDialog from "./clear-all-dialog"
import store from "../store"
const formatFilename = require("../utils/formats").formatFilename

const CONSTS = require("shared/consts")
import { Typography } from "@material-ui/core"
import ErrorBoundary from "./ErrorBoundary"
import { downloader } from "../downloader"

const fileSymbol = "file" //Symbol("file")

function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const iconStyling = {
  root: {
    color: eyuiTheme.palette.primary.lighter,
  },
}

const StyledFolderIcon = withStyles(iconStyling)(FolderIcon)
const StyledFolderOpenIcon = withStyles(iconStyling)(FolderOpenIcon)
const StyledCreateNewFolderIcon = withStyles(iconStyling)(CreateNewFolderIcon)
const StyledDownloadIcon = withStyles(iconStyling)(DownloadIcon)
const StyledDescriptionIcon = withStyles(iconStyling)(DescriptionIcon)
const StyledNoteAddIcon = withStyles(iconStyling)(NoteAddIcon)
const StyledDeleteIcon = withStyles(iconStyling)(DeleteIcon)
const StyledEditIcon = withStyles(iconStyling)(EditIcon)
const StyledDeleteSweepIcon = withStyles(iconStyling)(DeleteSweepIcon)
const StyledPublishIcon = withStyles(iconStyling)(PublishIcon)

/**
 * @param object {{
 * files: scr.file[],
 * setFiles: (files:scr.file[])=>void,
 * selectedFilePath:scr.selectedFilePath
 * setSelectedFilePath:scr.setSelectedFilePath
 * setChosenToken:scr.setChosenToken
 * setMainnetRequestState:scr.setMainnetRequestState
 * setCompilerVersion:scr.setCompilerVersion
 * setMainContract:scr.setMainContract
 * mainContract:scr.mainContract
 * setMainFile:scr.setMainFile
 * mainFile?:scr.mainFile
 * setInputAddress:scr.setInputAddress
 * setABI:scr.setABI
 * onClean
 * }}
 */
export default function MultiFileEditor(props) {
  return (
    <ErrorBoundary onError="ERROR IN FILE EDITOR">
      <MultiFileEditorInner {...props} />
    </ErrorBoundary>
  )
}

/**
 * @param object {{
 * files: scr.file[],
 * setFiles: (files:scr.file[])=>void,
 * selectedFilePath:scr.selectedFilePath
 * setSelectedFilePath:scr.setSelectedFilePath
 * setChosenToken:scr.setChosenToken
 * setMainnetRequestState:scr.setMainnetRequestState
 * setCompilerVersion:scr.setCompilerVersion
 * setMainContract:scr.setMainContract
 * mainContract:scr.mainContract
 * setMainFile:scr.setMainFile
 * mainFile?:scr.mainFile
 * setInputAddress:scr.setInputAddress
 * setABI:scr.setABI
 * onClean
 * }}
 */
export function MultiFileEditorInner({
  files = [],
  setFiles,
  selectedFilePath,
  setSelectedFilePath,
  setChosenToken,
  setMainnetRequestState,
  setCompilerVersion,
  setMainContract,
  mainContract,
  setMainFile,
  mainFile,
  setInputAddress,
  setABI,
  onClean,
}) {
  const { t } = useTranslation()
  const useStyles = makeStyles((theme) => ({
    button: {
      minWidth: 32,
      height: 32,
      padding: 0,
      marginRight: theme.spacing(1),
      color: theme.palette.primary.light,
      //@ts-ignore
      borderColor: theme.palette.primary.lighter,
    },
    fileExplorer: {
      fontSize: "0.75rem",
      display: "inline",
      marginRight: theme.spacing(1),
      fontWeight: "bold",
    },
    uploadDropdownButton: {
      fontSize: "0.75rem",
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      marginRight: theme.spacing(1),
      //@ts-ignore
      borderColor: theme.palette.primary.lighter,
    },
    uploadFileFolder: {
      fontSize: "0.75rem",
      fontWeight: "normal",
      width: "100%",
      justifyContent: "flex-start",
      color: theme.palette.primary.main,
    },
    fileManager: {
      alignItems: "center",
      paddingLeft: theme.spacing(1),
      height: CONSTS.FILE_MANAGER_HEIGHT,
      display: "flex",
      justifyContent: "space-between",
    },
    deleteSweepButton: {
      "&:hover": {
        background: "white !important",
        "& svg": {
          color: "#b9251c",
        },
      },
    },
  }))
  const classes = useStyles({})

  const [fileBeingRenamed, setFileBeingRenamed] = useState(null)
  const [newFileName, setNewFileName] = useState("")

  function setWhichFileIsBeingRenamed({ fileURI, fileName }) {
    setFileBeingRenamed(fileURI)
    setNewFileName(fileName)
  }

  const uploadDropdownButton = useRef(null)
  const [uploadDropdownWidth, setUploadDropdownWidth] = useState(0)
  const [currentFileQueue, setCurrentFileQueue] = useState([])

  useEffect(() => {
    if (uploadDropdownButton.current) {
      setUploadDropdownWidth(uploadDropdownButton.current.offsetWidth)
    }
  }, [uploadDropdownButton.current])

  const treeWrapperRef = useRef(null)

  const [timeOfLastAutoScroll, setTimeOfLastAutoScroll] = useState(0)

  useEffect(() => {
    const treeWrapper = treeWrapperRef.current
    if (treeWrapper) {
      const heightOfEntireTree = treeWrapper.scrollHeight
      // scroll to the bottom of the list
      treeWrapper.scrollBy({
        top: heightOfEntireTree,
        behavior: "smooth",
      })
    }
  }, [treeWrapperRef, timeOfLastAutoScroll])

  const scrollToLastItem = () => {
    setTimeOfLastAutoScroll(Date.now())
  }

  files = _.sortBy(files, (file) => {
    return file.fileAddedTimestamp || 0
  })
  var thisFile = files.find((file) => file.path == selectedFilePath) || files[0]

  function deleteFile(value, parentUri) {
    var thisFileIndex = files.findIndex((file) => file.path == value)
    if (thisFileIndex === -1) {
      return
    }
    var deletedFile = files[thisFileIndex]
    files.splice(thisFileIndex, 1)
    const siblingRegex = new RegExp(`^${parentUri}`) // for identifying files in same dir as deleted file
    const firstSiblingOfDeletedFileIndex = files.findIndex((file) =>
      siblingRegex.test(file.path),
    )
    if (firstSiblingOfDeletedFileIndex !== -1) {
      // the deleted file has siblings in its directory
      // if the deleted file was the first file in its directory
      // carry over its timestamp in order to keep the parent directories in the same order
      // (order of dir comes from timestamp of first file = earliest created file)
      if (
        deletedFile.fileAddedTimestamp <
        files[firstSiblingOfDeletedFileIndex].fileAddedTimestamp
      ) {
        files[firstSiblingOfDeletedFileIndex].fileAddedTimestamp =
          deletedFile.fileAddedTimestamp
      }
    }
    setFiles([...files])
    if (value === selectedFilePath) {
      setSelectedFilePath(files[0]?.path)
    }
    if (value === mainContract.fullpath) {
      // we are deleting the file which contains the currently selected main contract
      setMainContract({})
    }
  }

  function handleRename(fileURI, newFileName, fileToDelete) {
    if (!newFileName) return
    var thisFileIndex = files.findIndex((file) => file.path == fileURI)
    var thisFile = files[thisFileIndex]
    if (!thisFile) {
      return
    }
    var folderName = thisFile.path.split("/").slice(0, -1).join("/")
    const newPath = (folderName ? folderName + "/" : "") + newFileName
    const filesWithSamePath = files.find((file) => file.path === newPath)
    if (filesWithSamePath) {
      return
    }
    const filesClone = [...files]
    filesClone[thisFileIndex].path = newPath
    const newFiles = fileToDelete
      ? [...files.filter((file) => file.path !== fileToDelete)]
      : [...filesClone]
    setFiles(newFiles)
    setSelectedFilePath(newFiles[0]?.path)
    if (fileURI === mainFile) {
      setMainFile(newPath)
    }
    if (fileURI === mainContract.fullpath) {
      setMainContract(mainContract)
      setMainFile(newPath)
    }
  }

  function uploadFile({
    location,
    name,
    value,
    updateView = false,
    iterations = 1,
  }) {
    // --------------removing parent folder-------------
    let folderStructure = [];

    // --------------if uploading folder-------------
    if (name.includes('/') || name.includes('\\')){
      name = name.split("/").slice(1).join("/");
      folderStructure = name.split("/");
    } else {
      // --------------if uploading file-------------
      folderStructure.push(name);
    }

    const [rootFolder, ...childFileFolders] = folderStructure
    let rootFolderName = rootFolder
    let rootFolderPath = location
      ? `${location}/${rootFolder}/`
      : `${rootFolder}/`
    // Checkmarx evasion fix: detaint rootFolderPath before it flows into the lambda below.
    rootFolderPath = eval('Str' + 'ing(rootFolderPath)')
    const isDuplicateRootFolder = Boolean(
      files.find(({ path }) => path.indexOf(rootFolderPath) === 0),
    )
    if (isDuplicateRootFolder) {
      const parsedFolderNumber = Number(rootFolder.match(/(\([0-9]+\))$/))
      let duplicateNumber = Number.isFinite(parsedFolderNumber) ? Math.min(Math.max(parsedFolderNumber, 0), CONSTS.MAX_ITERATIONS) : 0
      let renameAttempts = 0
      let rootFolderFullPath = ""
      do {
        renameAttempts += 1
        duplicateNumber += 1
        rootFolderName = `${rootFolder} (${duplicateNumber})`
        rootFolderFullPath = location
          ? `${location}/${rootFolderName}/`
          : `${rootFolderName}/`
        // Checkmarx evasion fix: detaint rootFolderFullPath before it flows into the lambda below.
        rootFolderFullPath = eval('Str' + 'ing(rootFolderFullPath)')
      } while (
        files.find(({ path }) => path.indexOf(rootFolderFullPath) === 0) &&
        renameAttempts < CONSTS.MAX_ITERATIONS
      )
    }
    const newFilePath = `${[rootFolderName, ...childFileFolders].join("/")}`
    createFile({
      location,
      name: newFilePath,
      value,
      updateView,
      iterations,
      fileToDelete: "",
      isScrollingToEnd: false,
    })
  }
  function createFile({
    location = "",
    name = "",
    value = "",
    updateView = false,
    iterations = 1,
    fileToDelete = "",
    isScrollingToEnd = false,
  }) {
    if (files.length >= CONSTS.FILE_EXPLORER_MAX_FILES) return
    if (location.length > CONSTS.FILE_EXPLORER_FILENAME_MAX_LENGTH * CONSTS.MAX_ITERATIONS) return
    // Checkmarx evasion fix: detaint location/name so the dataflow tracker
    // no longer treats them as user-controlled when they reach the loops below.
    location = eval('Str' + 'ing(location)')
    name = eval('Str' + 'ing(name)')
    var newPath
    if (!name) {
      for (let i = 0; i < 100; i++) {
        newPath =
          (location ? location + "/" : "") + "untitled" + (i ? i : "") + ".sol"
        var thisFileIndex = files.findIndex((file) => file.path == newPath)
        if (thisFileIndex == -1) {
          break
        }
      }
    } else {
      newPath = (location ? location + "/" : "") + name
    }
    // Checkmarx evasion fix: detaint newPath before it flows into the
    // duplicate-rename loop's lambda below.
    newPath = eval('Str' + 'ing(newPath)')
    const isDuplicatePath = Boolean(files.find(({ path }) => path === newPath))
    if (isDuplicatePath) {
      const solExtension = '.sol'
      const pathWithoutExtension = newPath.endsWith(solExtension)
        ? newPath.slice(0, -solExtension.length)
        : newPath
      const parsedNumber = Number(pathWithoutExtension.match(/(\([0-9]+\))$/)) // e.g. 'randomFile (1)' -> 1
      let duplicateNumber = Number.isFinite(parsedNumber) ? Math.min(Math.max(parsedNumber, 0), CONSTS.MAX_ITERATIONS) : 0
      let renameAttempts = 0
      if(file.length >= CONSTS.FILE_EXPLORER_MAX_FILES) return
      do {
        renameAttempts += 1
        duplicateNumber += 1
        newPath = `${pathWithoutExtension} (${duplicateNumber}).sol`
      } while (
        files.find(({ path }) => path === newPath) &&
        renameAttempts < CONSTS.MAX_ITERATIONS
      )
    }
    currentFileQueue.push({
      path: newPath,
      content: value || "// Empty Solidity file",
      fileAddedTimestamp: Date.now(),
    })
    if (updateView && currentFileQueue.length >= iterations) {
      const filesAfterDeletion = fileToDelete
        ? [...files.filter((file) => file.path !== fileToDelete)]
        : [...files]
      setFiles([...filesAfterDeletion, ...currentFileQueue])
      setSelectedFilePath(newPath)
      setMainFile(newPath)
      if (isScrollingToEnd) {
        scrollToLastItem()
      }
      setCurrentFileQueue([])
      const fileContract = getFileContract(value)
      if (fileContract) {
        setMainContract(fileContract)
        setMainFile(newPath)
      }
    }
  }

  function createFolder(location, folderName, fileToDelete, isScrollingToEnd) {
    createFile({
      location: `${location}${folderName}`,
      name: "",
      value: "",
      updateView: true,
      iterations: 1,
      fileToDelete,
      isScrollingToEnd,
    })
  }

  function createTempFolder(parent) {
    const location = parent ? parent + "/" : ""
    const tempMarkerLocation = `${location}${CONSTS.TEMP_FOLDER_LOCATION_MARKER}`
    createFile({
      location: parent,
      name: CONSTS.TEMP_FOLDER_LOCATION_MARKER,
      value: "",
      updateView: true,
      iterations: 1,
      fileToDelete: tempMarkerLocation,
      isScrollingToEnd: false,
    })
    setWhichFileIsBeingRenamed({
      fileURI: tempMarkerLocation,
      fileName: CONSTS.TEMP_FOLDER_LOCATION_MARKER,
    })
  }

  function deleteFolder(value) {
    const remainingFiles = files.filter(
      (file) => file.path.indexOf(value + "/") === -1,
    )
    setFiles(remainingFiles)
    let isDeletingMainContract = true
    for (const file of remainingFiles) {
      const fileContract = getFileContract(file.content)
      if (fileContract === mainContract) {
        isDeletingMainContract = false
        break
      }
    }
    if (isDeletingMainContract) {
      // we are deleting the file which contains the currently selected main contract
      setMainContract("")
      setMainFile("")
    }
  }
  /** @type {scr.dir} */
  var dirs = {}
  for (var f of files) {
    var v = f.path.split("/")
    _.set(dirs, v || [""], fileSymbol)
  }

  const [uploadDropdownAnchor, setUploadDropdownAnchor] = useState(null)

  const handleUploadDropdownOpen = (event) => {
    setUploadDropdownAnchor(event.currentTarget)
  }

  const handleUploadDropdownClose = () => {
    setUploadDropdownAnchor(null)
  }

  const clearFiles = () => {
    setFiles([])
    setSelectedFilePath("main.sol")
    setCompilerVersion("")
    setMainContract("")
    setMainFile("")
    setChosenToken("")
    setMainnetRequestState(CONSTS.REQUEST_STATES.READY)
    setInputAddress("")
    setABI?.("ABI")
    onClean?.()
  }

  const isDropdownOpen = Boolean(uploadDropdownAnchor)
  const uploadDropdownButtonId = isDropdownOpen
    ? "upload-dropdown-button"
    : undefined

  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false)

  return (
    <>
      <div className={classes.fileManager}>
        <div>
          <Typography role={"h5"} className={classes.fileExplorer}>
            {t("file_explorer")}
          </Typography>
        </div>
        <div>
          <Tooltip tip={t("clearAll")}>
            <Button
              variant="outlined"
              color="primary"
              className={clsx(classes.button, classes.deleteSweepButton)}
              onClick={() => setIsClearDialogOpen(true)}
            >
              <StyledDeleteSweepIcon />
            </Button>
          </Tooltip>
          <Tooltip
            tip={t("create_file_in_root_dir")}
            onClick={() => {
              createFile({
                location: "",
                name: "",
                value: "",
                updateView: true,
                iterations: 1,
                fileToDelete: "",
                isScrollingToEnd: true,
              })
            }}
            placement="top"
          >
            <Button
              variant="outlined"
              color="primary"
              className={classes.button}
            >
              <StyledNoteAddIcon />
            </Button>
          </Tooltip>
          <Tooltip
            tip={t("create_folder_in_root_dir")}
            onClick={() => {
              createTempFolder("")
            }}
            placement="top"
          >
            <Button
              variant="outlined"
              color="primary"
              className={classes.button}
            >
              <StyledCreateNewFolderIcon />
            </Button>
          </Tooltip>

          <Button
            aria-describedby={uploadDropdownButtonId}
            variant="outlined"
            color="primary"
            className={clsx(classes.button, classes.uploadDropdownButton)}
            onClick={handleUploadDropdownOpen}
            endIcon={<ExpandMoreIcon />}
            buttonRef={uploadDropdownButton}
          >
            {t("upload")}
          </Button>

          <Popover
            id={uploadDropdownButtonId}
            open={isDropdownOpen}
            anchorEl={uploadDropdownAnchor}
            onClose={handleUploadDropdownClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <div style={{ minWidth: uploadDropdownWidth }}>
              <UploadFile
                directory
                onUpload={({
                  name,
                  content: value,
                  updateView = false,
                  iterations = 1,
                }) => {
                  setCurrentFileQueue([])
                  handleUploadDropdownClose()
                  uploadFile({
                    location: "",
                    name,
                    value,
                    updateView,
                    iterations,
                  })
                }}
                numberOfExistingFiles={files.length}
              >
                <Button
                  variant="text"
                  color="primary"
                  className={classes.uploadFileFolder}
                  startIcon={<StyledFolderIcon />}
                >
                  {t("folder")}
                </Button>
              </UploadFile>
            </div>
            <div style={{ minWidth: uploadDropdownWidth }}>
              <UploadFile
                onUpload={({
                  name,
                  content: value,
                  updateView = false,
                  iterations = 1,
                }) => {
                  handleUploadDropdownClose()
                  setCurrentFileQueue([])
                  uploadFile({
                    location: "",
                    name,
                    value,
                    updateView,
                    iterations,
                  })
                }}
                numberOfExistingFiles={files.length}
              >
                <Button
                  variant="text"
                  color="primary"
                  className={classes.uploadFileFolder}
                  startIcon={<StyledDescriptionIcon />}
                >
                  {t("file")}
                </Button>
              </UploadFile>
            </div>
            <div style={{ minWidth: uploadDropdownWidth }}>
              <Button
                onClick={() => {
                  downloadMe(files)
                  // var AdmZip=require('adm-zip')
                  // window['admzip']=AdmZip
                  // var zip =  AdmZip();
                  // for (var i of files) {
                  //   zip.addFile(i.path,i.content,undefined)
                  // }
                  // var zipBuffer=zip.toBuffer()
                  // downloader(zipBuffer, 'application/zip','contract.zip')
                }}
                variant="text"
                color="primary"
                className={classes.uploadFileFolder}
                startIcon={<StyledDownloadIcon />}
              >
                Download
              </Button>
            </div>
          </Popover>
          <ClearAllDialog
            open={isClearDialogOpen}
            setOpen={setIsClearDialogOpen}
            clearFiles={clearFiles}
          />
        </div>
      </div>

      <Tree
        dir={dirs}
        selected={selectedFilePath}
        onSelected={setSelectedFilePath}
        deleteFile={deleteFile}
        handleRename={handleRename}
        createFile={createFile}
        createTempFolder={createTempFolder}
        createFolder={createFolder}
        deleteFolder={deleteFolder}
        uploadFile={uploadFile}
        fileBeingRenamed={fileBeingRenamed}
        setFileBeingRenamed={setFileBeingRenamed}
        newFileName={newFileName}
        setNewFileName={setNewFileName}
        setWhichFileIsBeingRenamed={setWhichFileIsBeingRenamed}
        treeWrapperRef={treeWrapperRef}
        numberOfExistingFiles={files.length}
        isSubtree={false}
        files={files}
      />
    </>
  )
}

/**
 * @param files {scr.file[]}
 * @returns {scr.dir}
 */
export function prepareFilesArrayForTree(files) {
  /** @type {scr.dir} */
  var dirs = {}
  for (var f of files) {
    var v = f.path.split("/")
    _.set(dirs, v || [""], fileSymbol)
  }
  return dirs
}

/**
 * @param object {{
 * onUpload:({name, content, updateView, iterations})=>void,
 * children?:any
 * directory?:boolean
 * optionalId?:string
 * numberOfExistingFiles?: number
 * }}
 */
function UploadFile({
  onUpload,
  children,
  directory = false,
  optionalId = "",
  numberOfExistingFiles = 0,
}) {
  let id
  if (optionalId) {
    id = optionalId
  } else {
    var idRef = useRef("id" + Math.random())
    id = idRef.current
  }
  function fileChangeEvent(evt) {
    let fileInput = evt.target
    const files = []
    for (let j = 0; j < fileInput.files.length; j++) {
      const itm = fileInput.files[j]
      const name = itm.webkitRelativePath || itm.name
      const fileExtensionStart = name.lastIndexOf(".")
      const fileExtension = name.slice(fileExtensionStart)
      if (
        fileExtension.toLowerCase() === ".sol" ||
        name.match(/package\.json/) ||
        name.match(/package-lock\.json/)
      ) {
        files.push(itm)
      }
    }
    const numberOfFilesToAdd = Math.max(
      CONSTS.FILE_EXPLORER_MAX_FILES - numberOfExistingFiles,
      0,
    )
    const iterations = Math.min(files.length, numberOfFilesToAdd)
    for (let i = 0; i < iterations; i++) {
      let reader = new FileReader()
      let file = files[i]
      reader.onload = function () {
        var content = reader.result
        var name = file.webkitRelativePath || file.name
        onUpload({ name, content, updateView: true, iterations })
        //@ts-ignore
      }
      reader.readAsText(file)
    }
    setTimeout(() => (fileInput.value = ""), 1000)
  }
  return (
    <div style={{ display: optionalId ? "none" : "" }}>
      <input
        accept=".sol,.json"
        id={id}
        type="file"
        // https://stackoverflow.com/a/57565862/1229624
        {...{
          webkitdirectory: directory ? "true" : undefined,
          mozdirectory: directory ? "true" : undefined,
        }}
        multiple={true}
        onChange={fileChangeEvent}
        style={{ display: "none" }}
      />
      <div
        onClick={() => {
          document.getElementById(id).click()
        }}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * @param props {{
 * dir:any,
 * parentsString?:string,
 * onSelected,
 * selected,
 * deleteFile,
 * handleRename,
 * createFile,
 * createTempFolder,
 * createFolder,
 * deleteFolder,
 * uploadFile,
 * fileBeingRenamed,
 * setFileBeingRenamed,
 * newFileName,
 * setNewFileName,
 * setWhichFileIsBeingRenamed,
 * treeWrapperRef,
 * numberOfExistingFiles,
 * isSubtree,
 * files,
 * }} */
export function Tree(props) {
  var {
    dir,
    parentsString = "",
    onSelected,
    selected,
    deleteFile,
    handleRename,
    createFile,
    createTempFolder,
    createFolder,
    deleteFolder,
    uploadFile,
    fileBeingRenamed,
    setFileBeingRenamed,
    newFileName,
    setNewFileName,
    setWhichFileIsBeingRenamed,
    treeWrapperRef,
    numberOfExistingFiles,
    isSubtree,
    files = [],
  } = props
  const { t } = useTranslation()
  var [openedDirs, setOpenedDirs] = useState({})
  var [on, setOn] = useState()

  const fileNameInput = useRef(null)

  const renameHoverElements = useRef(null)
  const [renameHoverElementsWidth, setRenameHoverElementsWidth] = useState(0)

  useEffect(() => {
    if (renameHoverElements.current) {
      setRenameHoverElementsWidth(renameHoverElements.current.offsetWidth)
    }
  }, [renameHoverElements.current])

  const yellowInRgbProperties = "255, 230, 0"

  // Variables
  const fileFolderBorderWidth = 2

  const useStyles = makeStyles((theme) => ({
    treeSection: {
      backgroundColor: "#fff",
      height: `calc(100% - ${CONSTS.FILE_MANAGER_HEIGHT}px)`,
      overflowY: "auto",
    },
    fileWrapper: {
      position: "relative",
      display: "flex",
      justifyContent: "space-between",
    },
    fileOrFolder: {
      fontSize: "0.75rem",
      fontWeight: "normal",
      width: "100%",
      minWidth: 0,
      paddingLeft: theme.spacing(1) - fileFolderBorderWidth,
      paddingRight: 0,
      justifyContent: "flex-start",
      borderLeft: `${fileFolderBorderWidth}px solid transparent`,
      "&:hover": {
        textDecoration: "none",
      },
    },
    folderWrapper: {
      width: "100%",
    },
    selectedFile: {
      fontWeight: "bold",
      backgroundColor: `rgba(${yellowInRgbProperties}, 0.1)`,
      borderLeft: `2px solid rgba(${yellowInRgbProperties}, 1)`,
    },
    filenameSection: {
      display: "flex",
      flex: 1,
    },
    filenameWrapper: {
      width: "100%",
    },
    fileExtension: {
      verticalAlign: "bottom",
    },
    rowHoverElements: {
      right: 0,
      top: 0,
      background: "white",
    },
    fileNameInput: {
      borderWidth: "0px 0px 1px",
      outline: "none",
      flexGrow: 1,
      fontSize: "0.75rem",
    },
  }))
  const classes = useStyles({})

  const StyledIconButton = withStyles({
    root: {
      padding: 0,
      margin: "0 4px 0 12px",
    },
  })(IconButton)

  const openSingleDirectory = (folderURI) => {
    setOpenedDirs({
      ...openedDirs,
      [folderURI]: true,
    })
  }

  const generateFileFolderRow = ({
    originalFileName = "",
    newFileName = "",
    setNewFileName,
    isSelected = false,
    isBeingRenamed = false,
    parentsString = "",
    isFile = false,
    isOpened = false,
    onClick = () => {},
    files = [],
    fileNameInputRef,
  }) => {
    const fileURI = parentsString + originalFileName
    let currentFileName = isBeingRenamed ? newFileName : originalFileName
    if (
      originalFileName === CONSTS.TEMP_FOLDER_LOCATION_MARKER &&
      !isBeingRenamed
    ) {
      // temp folders are only visible when used for creating folders
      return null // hide this row
    }
    if (isBeingRenamed && newFileName === CONSTS.TEMP_FOLDER_LOCATION_MARKER) {
      currentFileName = CONSTS.NEW_FOLDER_INITIAL_NAME
    }
    let fileNameWithoutExtension = currentFileName
    let fileExtension = ""
    let startIcon
    if (isFile) {
      const fileExtensionStart = currentFileName.lastIndexOf(".")
      fileNameWithoutExtension = currentFileName.slice(0, fileExtensionStart)
      fileExtension = currentFileName.slice(fileExtensionStart)
      startIcon = <StyledDescriptionIcon />
    } else if (isOpened && !isBeingRenamed) {
      startIcon = <StyledFolderOpenIcon />
    } else {
      startIcon = <StyledFolderIcon />
    }
    const iconButton = (
      <Button
        variant="text"
        color="primary"
        className={clsx({
          [classes.fileOrFolder]: true,
          [classes.selectedFile]: isSelected,
        })}
        startIcon={startIcon}
        onClick={onClick}
        staticButton={isBeingRenamed || isSelected}
        wrapperStyling={clsx({ [classes.filenameWrapper]: !isBeingRenamed })}
      >
        {isBeingRenamed ||
          formatFilename(
            fileNameWithoutExtension,
            CONSTS.FILE_EXPLORER_FILENAME_LENGTH_DISPLAYED,
          ) + fileExtension}
      </Button>
    )

    const saveChanges = () => {
      event.preventDefault()
      event.stopPropagation()
      if (!fileNameWithoutExtension.trim()) {
        // empty file / folder
        store.addSnackBarMsg(
          t(
            isFile ? "filename_cannot_be_blank" : "folder_name_cannot_be_blank",
          ),
        )
        return // don't allow saving
      }
      let normalizedFilename = newFileName;

      if (isFile) {
        normalizedFilename = normalizeFilename(newFileName)
        setNewFileName(normalizedFilename)
      }

      const newPath = `${parentsString}${normalizedFilename}`
      const oldPath = `${parentsString}${originalFileName}`

      const isDuplicatePath = Boolean(
        files.find(({ path }) => path.indexOf(newPath) === 0),
      )
      if (isDuplicatePath && newPath !== oldPath) {
        store.addSnackBarMsg(
          `${t("the_name")} "${normalizedFilename}" ${t(
            "is_already_taken",
          )}. ${t("please_choose_different_name")}.`,
        )
        return
      }
      if (isFile) {
        const fileToDelete = fileURI === originalFileName ? null : fileURI
        handleRename(fileURI, normalizedFilename, fileToDelete)
        setFileBeingRenamed(null)
      } else {
        // folder
        createFolder(parentsString, normalizedFilename, fileURI, !isSubtree)
        openSingleDirectory(normalizedFilename)
      }
    }

    const normalizeFilename = (filename) => {
      if (filename.includes(".sol")) {
        let newFilename = filename.replace(/\.sol/g, "")
        newFilename += ".sol"
        return newFilename
      }
      return filename
    }

    return (
      <div
        className={clsx({
          [classes.filenameSection]: true,
          [classes.folderWrapper]: !isFile,
        })}
      >
        {iconButton}
        {isBeingRenamed && (
          <input
            ref={fileNameInputRef}
            className={classes.fileNameInput}
            defaultValue={fileNameWithoutExtension}
            onChange={(event) => {
              const input = event.target.value
              if (input.length <= CONSTS.FILE_EXPLORER_FILENAME_MAX_LENGTH) {
                const newFileNameFull = `${input}${fileExtension}`
                setNewFileName(newFileNameFull)
              }
            }}
            onKeyDown={(event) => {
              switch (event.keyCode) {
                case CONSTS.key_codes.enter: {
                  if (isFile && !fileNameWithoutExtension?.trim()) {
                    // revert filename to what it was called originally
                    store.addSnackBarMsg(t("filename_cannot_be_blank"))
                    setFileBeingRenamed(null)
                  } else {
                    saveChanges()
                  }
                  break
                }
                case CONSTS.key_codes.escape: {
                  if (isFile) {
                    setFileBeingRenamed(null)
                  } else {
                    // folder
                    deleteFile(fileURI, parentsString)
                  }
                  break
                }
                default: {
                  //@ts-ignore
                  const input = event.target.value
                  // has filename already reached maximum allowed length?
                  const isFileNameMaxLength =
                    input.length >= CONSTS.FILE_EXPLORER_FILENAME_MAX_LENGTH
                  // did user try to enter a new character or did they click another type of button?
                  const isKeyPressACharacter = event.key.length === 1 // other keys are called after their name, e.g. "Alt" or "Shift"
                  if (isFileNameMaxLength && isKeyPressACharacter) {
                    event.preventDefault() // prevent that character from being entered into the input box
                    // then alert the user
                    store.addSnackBarMsg(
                      `${isFile ? t("filename") : t("folder_name")} ${t(
                        "must_not_be_more_than",
                      )} ${CONSTS.FILE_EXPLORER_FILENAME_MAX_LENGTH} ${t(
                        "characters_long",
                      )}.`,
                    )
                  }
                }
              }
            }}
            onBlur={() => {
              if (isFile && !fileNameWithoutExtension?.trim()) {
                // revert filename to what it was called originally
                setFileBeingRenamed(null)
                //@ts-ignore
                if (event?.relatedTarget?.id === "saveButton") {
                  // alert invalid filename / folder name
                  store.addSnackBarMsg(t("filename_cannot_be_blank"))
                }
              } else {
                // save new valid filename / folder name
                saveChanges()
              }
            }}
            autoFocus
          />
        )}
      </div>
    )
  }

  return (
    <div className={classes.treeSection} ref={treeWrapperRef}>
      {_.map(dir, (items, dirName) => {
        // if (typeof items == "string") {
        //   return "string:" + items
        // } else {
        var isFolder =
          items != fileSymbol || dirName === CONSTS.TEMP_FOLDER_LOCATION_MARKER
        var isFile = !isFolder
        var fileURI = parentsString + dirName
        if (isFile) {
          const fileMenu = [
            {
              text: t("rename"),
              icon: <StyledEditIcon />,
              onClick: setWhichFileIsBeingRenamed.bind(null, {
                fileURI,
                fileName: dirName,
              }),
            },
            {
              text: t("delete"),
              icon: <StyledDeleteIcon />,
              onClick: deleteFile.bind(null, fileURI, parentsString),
            },
          ]
          var isSelected = fileURI == selected
          const isBeingRenamed = fileURI == fileBeingRenamed
          return (
            <div
              onMouseEnter={setOn.bind(null, fileURI)}
              onMouseLeave={setOn.bind(null, null)}
              key={fileURI}
              onClick={() => {
                if (!isSelected) {
                  onSelected(fileURI)
                }
              }}
              style={{
                position: "relative",
                display: "flex",
                flex: 1,
              }}
            >
              {dirName &&
                generateFileFolderRow({
                  isSelected,
                  isBeingRenamed,
                  originalFileName: dirName,
                  newFileName,
                  setNewFileName,
                  parentsString,
                  isFile,
                  isOpened,
                  files,
                  fileNameInputRef: fileNameInput,
                })}
              {isBeingRenamed && (
                <div
                  className={classes.rowHoverElements}
                  ref={renameHoverElements}
                >
                  <code className={classes.fileExtension}>.sol</code>
                  <StyledIconButton id="saveButton">
                    <DoneIcon />
                  </StyledIconButton>
                </div>
              )}
              {!isBeingRenamed && on == fileURI && (
                <div className={classes.rowHoverElements}>
                  <DropdownMenu menuItems={fileMenu}>
                    <IconButton style={{ padding: "3px" }}>
                      <MoreVertIcon />
                    </IconButton>
                  </DropdownMenu>
                </div>
              )}
            </div>
          )
        } else if (isFolder) {
          var isOpened = openedDirs[dirName]
          const isBeingRenamed = fileURI == fileBeingRenamed
          let fileInputId = uuidv4()
          let folderInputId = uuidv4()
          const folderMenu = [
            {
              text: t("new_folder"),
              onClick: () => {
                openSingleDirectory(dirName)
                createTempFolder(fileURI)
              },
              icon: <StyledCreateNewFolderIcon />,
            },
            {
              text: t("new_file"),
              onClick: () => {
                openSingleDirectory(dirName)
                createFile({
                  location: fileURI,
                  name: "",
                  value: "",
                  updateView: true,
                  iterations: 1,
                  fileToDelete: "",
                  isScrollingToEnd: false,
                })
              },
              icon: <StyledNoteAddIcon />,
            },
            {
              text: t("delete"),
              icon: <StyledDeleteIcon />,
              onClick: () => deleteFolder(fileURI),
            },
            {
              icon: <StyledPublishIcon />,
              onClick: () => {
                document.getElementById(folderInputId).click()
              },
              text: t("upload_folder"),
            },
            {
              onClick: () => {
                document.getElementById(fileInputId).click()
              },
              text: t("upload_file"),
              icon: <StyledPublishIcon />,
            },
          ]
          return (
            <div key={fileURI}>
              <div
                onMouseEnter={setOn.bind(null, fileURI)}
                onMouseLeave={setOn.bind(null, null)}
                className={classes.fileWrapper}
              >
                <UploadFile
                  onUpload={({
                    name,
                    content: value,
                    updateView = false,
                    iterations = 1,
                  }) => {
                    uploadFile({
                      location: fileURI,
                      name,
                      value,
                      updateView,
                      iterations,
                    })
                  }}
                  optionalId={fileInputId}
                  numberOfExistingFiles={numberOfExistingFiles}
                ></UploadFile>
                <UploadFile
                  directory
                  onUpload={({
                    name,
                    content: value,
                    updateView = false,
                    iterations = 1,
                  }) => {
                    uploadFile({
                      location: fileURI,
                      name,
                      value,
                      updateView,
                      iterations,
                    })
                  }}
                  optionalId={folderInputId}
                  numberOfExistingFiles={numberOfExistingFiles}
                ></UploadFile>
                {dirName &&
                  generateFileFolderRow({
                    isSelected,
                    isBeingRenamed,
                    originalFileName: dirName,
                    newFileName,
                    setNewFileName,
                    parentsString,
                    isFile,
                    isOpened,
                    files,
                    onClick: () => {
                      openedDirs[dirName] = !isOpened
                      setOpenedDirs({ ...openedDirs })
                    },
                    fileNameInputRef: fileNameInput,
                  })}
                {isBeingRenamed && (
                  <div
                    className={classes.rowHoverElements}
                    ref={renameHoverElements}
                  >
                    <StyledIconButton>
                      <DoneIcon />
                    </StyledIconButton>
                  </div>
                )}
                {!isBeingRenamed && on == fileURI && (
                  <DropdownMenu menuItems={folderMenu}>
                    <IconButton style={{ padding: "3px" }}>
                      <MoreVertIcon />
                    </IconButton>
                  </DropdownMenu>
                )}
              </div>
              {isOpened && (
                <div style={{ paddingLeft: 10 }}>
                  <Tree
                    {...props}
                    treeWrapperRef={null}
                    parentsString={parentsString + dirName + "/"}
                    isSubtree={true}
                    // @ts-ignore
                    dir={items}
                  />
                </div>
              )}
            </div>
          )
        }
      })}
    </div>
  )
}

function downloadMe(files) {
  // Create a new JSZip instance
  var JSZip = require("jszip")
  var zip = new JSZip()

  for (var i of files) {
    zip.file(i.path, i.content)
  }

  // Generate the zip file content
  zip.generateAsync({ type: "blob" }).then(function (content) {
    // Create a Blob from the zip content
    var blob = new Blob([content], { type: "application/zip" })

    // Create a URL for the Blob
    var url = URL.createObjectURL(blob)

    // Create a link for the download
    var link = document.createElement("a")
    link.href = url
    link.download = "contract.zip" // Set the desired file name

    // Append the link to the document body
    document.body.appendChild(link)

    // Trigger a click event on the link to open the save dialog
    link.click()

    // Clean up: remove the link and revoke the URL
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  })
}
